import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { htmlToPlainText } from 'shared-helpers/common';
import { CategoryBadges } from 'components/common';
import { CourseLanguageVersionsFlags } from 'components/courses/common';
import { CoursesTileDropdown } from '.';

import type { CourseInView } from './Courses';
import type { PathStepItemCourse } from 'shared-types';

type Props =
  | {
      course: CourseInView;
      isCourseInPath?: false;
      preferredLanguageVersion: string;
      showDropdown?: boolean;
      updateCourses?: () => Promise<void>;
    }
  | {
      course: PathStepItemCourse['course'];
      isCourseInPath: true;
      preferredLanguageVersion: string;
      showDropdown?: false;
      updateCourses?: never;
    };

const CoursesTileContent = ({
  course,
  isCourseInPath = false,
  preferredLanguageVersion,
  showDropdown,
  updateCourses
}: Props) => {
  const { language, messages, serverConfig, theme } = useReduxState();

  const languageVersion = course.languageVersions?.[preferredLanguageVersion]
    ?.isPublished
    ? course.languageVersions?.[preferredLanguageVersion]
    : null;

  const courseTitle = languageVersion?.courseTitle || course.title;

  const courseDescription = languageVersion
    ? languageVersion.courseDescription || ''
    : course.description;

  return (
    <div className="content">
      <div className="header">
        <div className={`title ${showDropdown ? 'title-with-dropdown' : ''}`}>
          {courseTitle}
        </div>
        {showDropdown && !isCourseInPath ? (
          <div className="dropdown">
            <CoursesTileDropdown
              course={course as CourseInView}
              updateCourses={updateCourses}
            />
          </div>
        ) : null}
      </div>

      <CourseLanguageVersionsFlags course={course} onlyPublished={true} />

      <div className="info">
        {!isCourseInPath && (course as CourseInView).isMandatoryInView ? (
          <>
            <span className="info-mandatory">{messages.commonMandatory}</span>
            <span className="info-divider">{'•'}</span>
          </>
        ) : null}
        <span>
          {getMessageWithPlaceholders({
            message: messages.courseNumberOfChapters,
            placeholders: { numberOfChapters: course.chapters?.length || 0 }
          })}
        </span>

        {course.completionWorkloadValue &&
        (serverConfig.courseCompletionRequireWorkload ||
          course.completionWorkload) ? (
          <>
            <span className="info-divider">{'•'}</span>
            <span
              aria-label={messages.settingsCompletionWorkload}
              data-tooltip={messages.settingsCompletionWorkload}
            >
              {getMessageWithPlaceholders({
                message: messages.commonNumberOfHours,
                placeholders: {
                  numberOfHours: new Intl.NumberFormat(language).format(
                    course.completionWorkloadValue
                  )
                }
              })}
            </span>
          </>
        ) : null}
      </div>

      {courseDescription ? (
        <div className="description">{htmlToPlainText(courseDescription)}</div>
      ) : null}

      <CategoryBadges categories={course.categories} />

      <style jsx>{`
        .header {
          position: relative;
        }
        .title {
          color: ${theme.textColor};
          display: -webkit-box;
          font-size: 1.6rem;
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        .title-with-dropdown {
          padding-right: 40px;
        }
        .dropdown {
          position: absolute;
          right: 0;
          top: 0;
        }
        .info {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 600;
          margin-top: 3px;
        }
        .info-mandatory {
          color: ${theme.dangerColor};
        }
        .info-divider {
          margin: 0 7px;
        }
        .description {
          color: ${theme.textColorLight};
          display: -webkit-box;
          font-size: 1.4rem;
          margin-top: 5px;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        .footer {
          margin-top: auto;
          padding-top: 20px;
        }
        .footer-content {
          align-items: center;
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default CoursesTileContent;
